// Data
import { useAppSelector } from "core/store/reduxStore";
// UI
import { MessageRow } from "./message";
import { useEffect, useRef, useState } from "react";
import { ConversationType, MessageType } from "../chat.type";
import { UserClaims } from "core/contexts/auth/auth";
import { v4 as uuidv4 } from 'uuid';

interface MessageProps {
  amsUser: any;
  conversation?: ConversationType;
  containerWidthClasses: string;
}

export function Messages({ amsUser,conversation, containerWidthClasses }: MessageProps) {
  const convoState = useAppSelector((state) => state.chatConversations);
  const divRef: any = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [visibleMessages, setVisibleMessages] = useState(10); // Default to showing 10 most recent messages
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const updateContainerWidth = () => {
    if (divRef.current) {
      setContainerWidth(divRef.current.offsetWidth * 0.8);
    }
  };

  useEffect(() => {
    updateContainerWidth();

    window.addEventListener("resize", updateContainerWidth);
    return () => {
      window.removeEventListener("resize", updateContainerWidth);
    };
  }, [divRef]);

  let currentConversation;

  if (conversation) {
    currentConversation = conversation;
  } else {
    currentConversation = convoState.history[convoState.current];
  }

  // Handle showing more messages when user scrolls to top
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop } = e.currentTarget;
    // When user scrolls near the top, load more messages
    if (scrollTop < 100 && currentConversation?.messages?.length > visibleMessages) {
      setVisibleMessages((prev) => prev + 10);
    }
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [currentConversation?.messages?.length, convoState.loadingAiResp]);

  // This is a temporary message for the waiting dots
  const messageUser: MessageType = {
    _id: "1000000000",
    chatid: "1000000000",
    role: "assistant",
    model: "",
    content: "",
    createdAt: "",
    updatedAt: "",
    loading: false,
  };

  // Process message groups
  const processedMessageGroups = currentConversation?.messages
    ? currentConversation.messages
        .filter(message => message && message.chatid)
        .reduce((acc, message) => {
          const existingGroup = acc.find(group => group.chatid === message.chatid);

          if (existingGroup) {
            const isDuplicate = existingGroup.messages.some(
              m => m._id === message._id || (m.role === message.role && m.content === message.content)
            );
            
            if (!isDuplicate) {
              existingGroup.messages.push(message);
            }
          } else {
            acc.push({ chatid: message.chatid, messages: [message] });
          }

          return acc;
        }, [])
    : [];

  // Get only the most recent message groups based on visibleMessages count
  const visibleMessageGroups = processedMessageGroups.slice(-visibleMessages);

  return (
    <div 
      ref={divRef} 
      className={`transition-column mt-3 ms-3 me-3 ${containerWidthClasses} messages d-flex flex-column h-full`}
    >
      <div
        className="overflow-auto flex-grow"
        style={{ height: "calc(100% - 20px)" }} 
        onScroll={handleScroll}
      >
        {currentConversation && visibleMessageGroups.length > 0 && (
          <>
            {/* Show "Load More" button if there are more messages to load */}
            {processedMessageGroups.length > visibleMessages && (
              <div className="text-center mb-3">
                <button 
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setVisibleMessages(prev => prev + 10)}
                >
                  Load More Messages
                </button>
              </div>
            )}

            {/* Render visible message groups */}
            {visibleMessageGroups.map((group) => (
              <div key={group.chatid}>
                {group.messages.map((message) => {
                  if (!message) return null;
                  return (
                    <MessageRow
                      amsUser={amsUser}
                      key={message._id}
                      message={message}
                      userPrompt={typeof convoState.userInput === "string" ? convoState.userInput : ""}
                      loading={false}
                      conversationId={currentConversation.conversationId}
                      containerWidth={containerWidth}
                    />
                  );
                })}
              </div>
            ))}

            {/* Waiting dots indicator */}
            {convoState.loadingAiResp === "waiting" && (
              <MessageRow
                amsUser={amsUser}
                key={messageUser._id}
                message={messageUser}
                userPrompt={typeof convoState.userInput === "string" ? convoState.userInput : ""}
                loading={true}
                conversationId={currentConversation.conversationId}
                containerWidth={containerWidth}
              />
            )}

            {/* Invisible div for auto-scrolling to most recent messages */}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>
    </div>
  );
}